import { useLiveQuery } from "dexie-react-hooks";
import { type Card, db, type StudySession, type Topic, type Track } from "@/lib/db";
import { isDue } from "@/lib/fsrs";
import { type WeekProgress, weekProgress } from "@/lib/study";

const live = <T extends { deleted_at: string | null }>(r: T) => !r.deleted_at;

/** Tracks, active ones first, then by name. */
export function useTracks(): Track[] | undefined {
	return useLiveQuery(async () => {
		const rank = { active: 0, paused: 1, done: 2 } as const;
		return (await db.tracks.filter(live).toArray()).sort(
			(a, b) => rank[a.status] - rank[b.status] || a.name.localeCompare(b.name),
		);
	}, []);
}

/** A track's topics: milestones first, then in the order they were added. */
export function useTopics(trackId: string | null): Topic[] | undefined {
	return useLiveQuery(async () => {
		if (!trackId) return [];
		const rows = await db.topics.where("track_id").equals(trackId).filter(live).toArray();
		return rows.sort(
			(a, b) =>
				Number(b.milestone) - Number(a.milestone) || a.created_at.localeCompare(b.created_at),
		);
	}, [trackId]);
}

/** Newest study sessions for a track. */
export function useRecentStudy(trackId: string | null, limit = 8): StudySession[] | undefined {
	return useLiveQuery(async () => {
		if (!trackId) return [];
		const rows = await db.study_sessions.where("track_id").equals(trackId).filter(live).toArray();
		return rows
			.sort((a, b) => b.date.localeCompare(a.date) || b.created_at.localeCompare(a.created_at))
			.slice(0, limit);
	}, [trackId, limit]);
}

/** This week's progress for every track (D59), keyed by track id. */
export function useWeekProgress(week: string): Map<string, WeekProgress> | undefined {
	return useLiveQuery(async () => {
		const [tracks, study, focus] = await Promise.all([
			db.tracks.filter(live).toArray(),
			db.study_sessions.toArray(),
			db.focus_sessions.toArray(),
		]);
		return new Map(tracks.map((t) => [t.id, weekProgress(t, week, study, focus)]));
	}, [week]);
}

/** A track's cards, newest first (archived ones last). */
export function useTrackCards(trackId: string | null): Card[] | undefined {
	return useLiveQuery(async () => {
		if (!trackId) return [];
		const rows = await db.cards.where("track_id").equals(trackId).filter(live).toArray();
		return rows.sort(
			(a, b) => Number(a.archived) - Number(b.archived) || b.created_at.localeCompare(a.created_at),
		);
	}, [trackId]);
}

/**
 * Cards due at `now`, soonest first. Compared as instants rather than text
 * (server rows and local rows format timestamps slightly differently), and
 * `now` is bucketed to the minute so the list refreshes as time passes.
 */
export function useDueCards(now: Date, trackId?: string | null): Card[] | undefined {
	const minute = Math.floor(now.getTime() / 60_000);
	return useLiveQuery(async () => {
		const at = new Date(minute * 60_000);
		const due = await db.cards
			.filter((c) => isDue(c, at) && (!trackId || c.track_id === trackId))
			.toArray();
		return due.sort((a, b) => Date.parse(a.due_at) - Date.parse(b.due_at));
	}, [minute, trackId]);
}
